import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ScheduleListProps } from '../types';
import { getRangeForDay } from '../../../store/chatSchedule/utils';
import {
    makeSelectShedulesForRange,
    selectSchedulesError,
    selectSchedulesIsLoading,
    selectSchedulesIsSaving,
} from '../../../store/chatSchedule/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Typography } from '@mui/material';
import {
    requestAddSchedule,
    requestDeleteSchedule,
    requestUpdateSchedule,
    scheduleErrorSaving,
} from '../../../store/chatSchedule/actions';
import globalStrings from '../../../resources/strings';
import { DateRange } from '../../../store/chatSchedule/types';
import EditSchedule from './EditSchedule';

const emptyEditState = [false, null] as [boolean, string | null];

function dateToHourString(date: Date) {
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
}
const strings = globalStrings.chatSchedulePage.scheduleList;

const ScheduleList: React.FC<ScheduleListProps> = ({ day }) => {
    const today = new Date();
    const range = useMemo(() => getRangeForDay(day), [day.valueOf()]);
    const selectSchedules = useMemo(() => makeSelectShedulesForRange(range), [range]);
    const schedules = useSelector(selectSchedules);

    const [editState, setEditState] = useState(emptyEditState);
    const dispatch = useDispatch();
    const [isNew, editId] = editState;

    const handleCancel = useCallback(() => {
        setEditState(emptyEditState);
    }, []);
    const handleSave = useCallback(
        (period: DateRange) => {
            if (period.startTime >= period.endTime) {
                dispatch(scheduleErrorSaving(strings.startTimeEarlier));
                return;
            }
            if (schedules.some((s) => s.startTime < period.endTime && period.startTime < s.endTime && s.id != editId)) {
                dispatch(scheduleErrorSaving(strings.periodConflict));
                return;
            }
            if (isNew && period.endTime < new Date()) {
                dispatch(scheduleErrorSaving(strings.pastError));
                return;
            }

            if (isNew) {
                dispatch(requestAddSchedule(period));
            } else if (editId != null) {
                dispatch(requestUpdateSchedule(editId, period));
            }
        },
        [editState, schedules],
    );

    const isLoading = useSelector(selectSchedulesIsLoading);
    const isSaving = useSelector(selectSchedulesIsSaving);
    const error = useSelector(selectSchedulesError);
    useEffect(() => {
        if (error == null && !isSaving) {
            //save successful
            setEditState(emptyEditState);
        }
    }, [isSaving]);

    const handleDelete = useCallback((id) => {
        dispatch(requestDeleteSchedule(id));
    }, []);

    return (
        <>
            {schedules.map((s) => (
                <Box key={s.id} sx={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
                    {s.id != editId && (
                        <>
                            <Typography sx={{ fontSize: 14, mb: 2 }}>
                                {dateToHourString(s.startTime)} - {dateToHourString(s.endTime)}
                            </Typography>
                            <Button
                                disabled={isSaving || isLoading || s.endTime < today}
                                onClick={() => handleDelete(s.id)}
                            >
                                {strings.delete}
                            </Button>
                            <Button
                                disabled={isSaving || isLoading || s.endTime < today}
                                onClick={() => setEditState([false, s.id])}
                            >
                                {strings.edit}
                            </Button>
                        </>
                    )}
                    {editId == s.id && (
                        <EditSchedule
                            day={day}
                            initialValue={s}
                            onCancel={handleCancel}
                            onSave={handleSave}
                            disabled={isSaving || isLoading}
                        ></EditSchedule>
                    )}
                </Box>
            ))}
            <Button
                onClick={() => setEditState([true, null])}
                disabled={isNew || isSaving || isLoading || range.endTime < today}
            >
                {strings.add}
            </Button>
            {isNew && (
                <EditSchedule
                    day={day}
                    initialValue={null}
                    onCancel={handleCancel}
                    onSave={handleSave}
                    disabled={isSaving || isLoading}
                ></EditSchedule>
            )}
        </>
    );
};
export default ScheduleList;
